// OBT F3-C — Park raporu: park kimliğiyle OBT geçmişi özeti.
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Search } from 'lucide-react';
import { ObtLoading, ObtErrorState, ObtEmptyState, ObtStatTile } from '../ObtStates';
import { useParkReport } from './hooks/useParkReport';
import { summarizeParkHistory } from './reportMetrics';
import { formatDate } from './reportFormat';

export default function ParkReportPanel({ enabled = true }) {
  const [input, setInput] = useState('');
  const [parkId, setParkId] = useState('');
  const { loading, error, history, reload } = useParkReport(parkId, { enabled: enabled && Boolean(parkId) });

  const summary = history ? summarizeParkHistory(history) : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-2 p-4">
          <Label htmlFor="park-report-id">Park kimliği</Label>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <MapPin className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
              <Input
                id="park-report-id"
                value={input}
                placeholder="Park kimliğini girin…"
                className="pl-8"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && setParkId(input.trim())}
              />
            </div>
            <Button onClick={() => setParkId(input.trim())} disabled={!input.trim()}>
              <Search className="mr-2 h-4 w-4" aria-hidden="true" /> Getir
            </Button>
          </div>
          <p className="text-xs text-gray-400">
            Kapsam backend'de çözülür; yalnız erişim yetkiniz olan parkın geçmişi döner.
            Çapraz-park başarı sıralaması backend'de tanımlı değildir.
          </p>
        </CardContent>
      </Card>

      {!parkId && (
        <ObtEmptyState title="Park seçilmedi" description="Geçmişini görmek için bir park kimliği girin." />
      )}
      {parkId && loading && <ObtLoading label="Park geçmişi yükleniyor…" />}
      {parkId && !loading && error && <ObtErrorState error={error} onRetry={reload} />}

      {parkId && !loading && !error && history && (
        <>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" />
            <h3 className="text-base font-semibold text-gray-900">
              {history.park_name || 'Park'}
            </h3>
          </div>
          {summary && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <ObtStatTile label="Katıldığı bilgi talebi" value={summary.campaignCount} tone="info" />
              <ObtStatTile label="Cevaplanma sayısı" value={summary.responseCount} tone="info" />
              <ObtStatTile label="Revizyon sayısı" value={summary.revisionCount} tone="warning" />
              <ObtStatTile label="Yüklenen fotoğraf" value={summary.photoCount} tone="neutral" />
            </div>
          )}
          {summary && (
            <p className="text-sm text-gray-600">
              Son cevap tarihi: <span className="font-semibold">{formatDate(summary.lastSubmittedAt)}</span>
              {summary.lastCampaignTitle && (
                <> · Son bilgi talebi: <span className="font-semibold">{summary.lastCampaignTitle}</span></>
              )}
            </p>
          )}
          {summary && summary.responseCount === 0 && (
            <ObtEmptyState title="Bu park için OBT cevabı yok" />
          )}
        </>
      )}
    </div>
  );
}
